import { useEffect, useState, type JSX } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { BuscaProduto } from '../componentes/BuscaProduto.js';
import { ConfirmarTabela } from '../componentes/ConfirmarTabela.js';
import { itemQuePedeConferencia } from '../lib/classe-do-item.js';
import { rotuloPagamento, rotuloTabela } from '../lib/formato.js';
import { Carregando, Erro } from '../componentes/Estados.js';
import { DuplicarComo } from '../componentes/DuplicarComo.js';
import { LinhaItem } from '../componentes/LinhaItem.js';
import { ParametrosOrcamento } from '../componentes/ParametrosOrcamento.js';
import { moedaBr, useGerador, type ItemOrcamento } from '../estado/gerador.js';
import { baixarPdf } from '../lib/pdf.js';
import { mensagemDoErro } from '../lib/api.js';
import { baseDoModo, classeDoModo, modoDoOrcamento } from '../estado/modo.js';
import './Orcamento.css';

/**
 * Editor do orçamento — Story 8.3 (RF-21..26).
 *
 * Rascunho é editável; emitido é **imutável**, e a interface reflete isso: os
 * campos viram texto, a busca de produto some e as ações passam a ser duplicar
 * e baixar o PDF. Quem decide é o `editavel` que o servidor manda — a UI não
 * reimplementa a regra do RF-25, só obedece.
 */
export function OrcamentoEditor(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const navegar = useNavigate();

  const {
    orcamento,
    carregando,
    salvando,
    emitindo,
    erro,
    carregar,
    adicionarItem,
    removerItem,
    emitir,
    duplicar,
    limpar,
  } = useGerador();

  const [avisoEmissao, setAvisoEmissao] = useState<string | undefined>(undefined);
  const [erroAcao, setErroAcao] = useState<unknown>(undefined);
  const [baixando, setBaixando] = useState(false);
  /** O diálogo "Duplicar como…" está aberto. */
  const [duplicando, setDuplicando] = useState(false);
  /** A tabela que o "Duplicar como…" já abre marcada — S12, vindo do aviso. */
  const [tabelaDuplicar, setTabelaDuplicar] = useState<string | undefined>(undefined);
  /** O aviso "Confira a tabela" aberto, com o item do exemplo — S12, CA-6. */
  const [conferindo, setConferindo] = useState<ItemOrcamento | undefined>(undefined);

  useEffect(() => {
    // Estado local também zera ao trocar de orçamento: sem isto, o aviso
    // "ORC-… emitido" e o erro da tela anterior acompanhavam o usuário até a
    // duplicata recém-aberta, que é justamente um rascunho novo e vazio.
    setAvisoEmissao(undefined);
    setErroAcao(undefined);
    setConferindo(undefined);
    setTabelaDuplicar(undefined);
    if (id !== undefined) void carregar(id);
    return () => {
      limpar();
    };
  }, [id, carregar, limpar]);

  if (carregando && orcamento === undefined) {
    return (
      <div className="cartao">
        <Carregando rotulo="Abrindo orçamento…" />
      </div>
    );
  }

  if (orcamento === undefined) {
    return (
      <div className="cartao">
        <Erro
          erro={erro}
          aoRepetir={() => {
            if (id !== undefined) void carregar(id);
          }}
        />
      </div>
    );
  }

  const editavel = orcamento.editavel;
  const semPreco = orcamento.semPreco ?? [];
  /*
   * O modo sai do SERVIDOR (a tabela do orçamento), nunca da rota.
   *
   * Um del credere aberto por um link antigo `/orcamentos/:id` tem de
   * mostrar o seletor do mesmo jeito — senão o vendedor editaria preço sem
   * saber qual coluna está valendo. É a mesma regra do `editavel`: quem
   * decide é o dado, a UI obedece. A rota `/del-credere/:id` existe só para
   * o item do menu continuar ativo enquanto se edita.
   */
  const modo = modoDoOrcamento(orcamento.tabela, orcamento.segmento);
  const delCredere = modo === 'del_credere';
  /*
   * A seção fica numa constante porque o TS não a estreita dentro das funções
   * abaixo: `orcamento` é do estado e pode virar `undefined` entre renders.
   * Duplicar mantém a seção — o pedido nasce onde o original vive.
   */
  const segmentoAtual = orcamento.segmento ?? null;
  const classeDaSecao = classeDoModo(modo);

  /**
   * O clique em "Emitir" — S12: antes, o cenário do ORC-2026-0053 pede
   * confirmação.
   *
   * O orçamento é lido da STORE no clique, e não do render: o botão fica travado
   * enquanto um PATCH de RT está no ar (a trava da corrida blur/Emitir), então o
   * que a store tem aqui é o RT já gravado — o mesmo que a emissão vai congelar.
   */
  function aoClicarEmitir(): void {
    const atual = useGerador.getState().orcamento;
    const exemplo = atual === undefined ? undefined : itemQuePedeConferencia(atual);
    if (exemplo !== undefined) {
      setConferindo(exemplo);
      return;
    }
    void aoEmitir();
  }

  async function aoEmitir(): Promise<void> {
    setErroAcao(undefined);
    try {
      const r = await emitir();
      setAvisoEmissao(
        `Orçamento ${r.numero} emitido. A partir de agora ele não pode ser alterado.`,
      );
    } catch (e) {
      setErroAcao(e);
    }
  }

  /**
   * Abre o rascunho duplicado NA SEÇÃO CERTA.
   *
   * Navegava fixo para `/orcamentos/:id`, o que sempre funcionou porque a
   * condição nunca mudava. Com a escolha, duplicar para o del credere levaria o
   * vendedor à seção errada — o orçamento abriria, mas fora da aba dele.
   */
  function abrirDuplicado(id: string, tabela: string): void {
    setDuplicando(false);
    setTabelaDuplicar(undefined);
    navegar(`${baseDoModo(modoDoOrcamento(tabela, segmentoAtual))}/${id}`);
  }

  async function aoBaixarPdf(): Promise<void> {
    if (orcamento === undefined) return;
    setErroAcao(undefined);
    setBaixando(true);
    try {
      await baixarPdf(orcamento.id, orcamento.numero ?? orcamento.id);
    } catch (e) {
      setErroAcao(e);
    } finally {
      setBaixando(false);
    }
  }

  return (
    <section className="pagina">
      <header className="pagina__cabecalho">
        <div>
          <h1>
            {orcamento.numero ?? 'Rascunho'}{' '}
            {editavel ? (
              <span className="etiqueta">Rascunho</span>
            ) : (
              <span className="etiqueta etiqueta--ok">Emitido</span>
            )}
          </h1>
          <p className="pagina__sub">
            {orcamento.clienteNome}
            {orcamento.clienteDocumento === null ? '' : ` · ${orcamento.clienteDocumento}`} ·{' '}
            {rotuloTabela(orcamento.tabela)} · {rotuloPagamento(orcamento.paymentType)}
            {delCredere && orcamento.delNivelPadrao != null && (
              <>
                {' · padrão '}
                {/*
                  O RÓTULO, não o código.
                  
                  Isto mostrava `del_5` cru — um identificador interno, no
                  cabeçalho que o vendedor lê para conferir o que está montando.
                  Ele não tem como saber que `del_5` é "5% · DEL 15%", e foi
                  justamente confusão de nomenclatura de coluna que o cliente
                  reportou.

                  O texto vem do servidor (fonte única, como na 9.4); enquanto a
                  lista não chega, mostra o código — que é o que já se via.
                */}
                <strong>{orcamento.delNivelPadraoRotulo ?? orcamento.delNivelPadrao}</strong>
              </>
            )}
          </p>

          {/*
            RT (%) e frete (R$/kg) — Story C.

            Aqui, e não na tabela: um RT e um frete governam o orçamento
            inteiro, como na aba `Variáveis` da planilha. Enquanto viveram como
            células por linha, em R$/m², a unidade não aparecia em lugar nenhum
            — e foi assim que o ORC-2026-0007 saiu R$ 66 mais barato.
          */}
          <ParametrosOrcamento orcamento={orcamento} editavel={editavel} />
        </div>

        <div className="pagina__acoes">
          {editavel ? (
            <button
              type="button"
              className="botao botao--primario"
              /*
                O `salvando` aqui NÃO é cosmético — é a primeira das duas travas
                da corrida blur/Emitir.

                O `mousedown` neste botão tira o foco do campo de RT/frete,
                disparando o PATCH; o `click` vem depois e chegaria antes da
                resposta. Como a emissão congela o preço lendo do BANCO, o
                snapshot sairia com o valor anterior — e o orçamento é imutável,
                com o PDF a caminho do cliente.

                A segunda trava está no `emitir()` da store, que aguarda os
                salvamentos em voo. Esta impede o clique; aquela protege
                qualquer outro caminho até a emissão.

                O resto: o servidor recusa emitir com item sem preço, e a tela
                diz isso antes do clique em vez de deixar descobrir no erro.
              */
              disabled={salvando || orcamento.itens.length === 0 || semPreco.length > 0}
              title={
                orcamento.itens.length === 0
                  ? 'Adicione ao menos um item para emitir'
                  : semPreco.length > 0
                    ? 'Resolva os itens sem preço antes de emitir'
                    : undefined
              }
              onClick={aoClicarEmitir}
            >
              {/* `emitindo`, não `salvando`: anunciar uma emissão que não
                  começou é pior que não anunciar — a ação é irreversível, e o
                  vendedor lê "Emitindo…" como "já foi". */}
              {emitindo ? 'Emitindo…' : 'Emitir orçamento'}
            </button>
          ) : (
            <>
              <button
                type="button"
                className="botao botao--neutro"
                disabled={baixando}
                onClick={() => {
                  void aoBaixarPdf();
                }}
              >
                {baixando ? 'Gerando PDF…' : 'Baixar PDF'}
              </button>
              <button
                type="button"
                className="botao botao--primario"
                disabled={salvando}
                onClick={() => {
                  setDuplicando(true);
                }}
              >
                Duplicar
              </button>
            </>
          )}
        </div>
      </header>

      {conferindo !== undefined && (
        <ConfirmarTabela
          exemplo={conferindo}
          aoVoltar={() => {
            setConferindo(undefined);
          }}
          aoContinuar={() => {
            setConferindo(undefined);
            void aoEmitir();
          }}
          aoDuplicar={() => {
            setConferindo(undefined);
            setTabelaDuplicar('RT10');
            setDuplicando(true);
          }}
        />
      )}

      {duplicando && (
        <DuplicarComo
          orcamento={orcamento}
          salvando={salvando}
          tabelaInicial={tabelaDuplicar}
          aoConfirmar={(condicao) => duplicar(condicao)}
          aoAbrir={abrirDuplicado}
          aoFechar={() => {
            setDuplicando(false);
            setTabelaDuplicar(undefined);
          }}
        />
      )}

      {avisoEmissao !== undefined && (
        <p className="nota nota--ok" role="status">
          {avisoEmissao}
        </p>
      )}

      {!editavel && (
        <p className="nota" role="note">
          Este orçamento foi emitido e está congelado com os preços da data de emissão. Para alterar
          alguma coisa, use <strong>Duplicar</strong> — o novo rascunho é recalculado com o preço
          vigente.
        </p>
      )}

      {(erroAcao ?? erro) !== undefined && (
        <p className="aviso" role="alert">
          {mensagemDoErro(erroAcao ?? erro)}
        </p>
      )}

      {/*
        Itens que o servidor não conseguiu precificar. Aparecem ANTES da tabela,
        em destaque, porque enquanto estiverem aqui a emissão é recusada — e a
        única saída é removê-los. Sem este bloco o orçamento parecia normal, com
        um total silenciosamente menor que a lista de itens.
      */}
      {semPreco.length > 0 && (
        <div className="cartao editor__sem-preco" role="alert">
          <h2 className="editor__sem-preco-titulo">
            {semPreco.length === 1
              ? '1 item sem preço na tabela deste orçamento'
              : `${String(semPreco.length)} itens sem preço na tabela deste orçamento`}
          </h2>
          <p className="editor__sem-preco-texto">
            A tabela <strong>{rotuloTabela(orcamento.tabela)}</strong> não tem preço para as
            referências abaixo, então elas ficam <strong>fora do total</strong> e o orçamento não
            pode ser emitido. Remova o item, informe um preço manual, ou troque a tabela do
            orçamento.
          </p>
          <ul className="editor__sem-preco-lista">
            {semPreco.map((i) => (
              <li key={i.itemId}>
                <span className="editor__sem-preco-ref">{i.referencia}</span>
                <span className="editor__sem-preco-qtd">{i.quantidade}</span>
                {editavel && (
                  <button
                    type="button"
                    className="botao botao--discreto"
                    disabled={salvando}
                    onClick={() => {
                      void removerItem(i.itemId);
                    }}
                  >
                    Remover
                  </button>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}

      {editavel && (
        <div className="cartao editor__adicionar">
          <BuscaProduto
            desabilitado={salvando}
            tabela={orcamento.tabela}
            /*
             * No del credere a marcação de preço depende da COLUNA: sem o
             * nível, a busca devolveria "sem preço" para todo produto e a
             * lista apareceria inteira desabilitada.
             */
            {...(delCredere && orcamento.delNivelPadrao != null
              ? { delNivel: orcamento.delNivelPadrao }
              : {})}
            /*
             * A busca oferece a classe da SEÇÃO — S5.
             *
             * É o que faz a tela ser de vinílicos ou de rodapé: na seção comum
             * e no del credere não vai classe, e a busca continua oferecendo o
             * catálogo inteiro, como sempre ofereceu.
             */
            {...(classeDaSecao === undefined ? {} : { classe: classeDaSecao })}
            aoEscolher={(p) => {
              // Quantidade 1 como ponto de partida; o vendedor ajusta na linha.
              void adicionarItem(p.id, '1');
            }}
          />
        </div>
      )}

      <div className="cartao editor__quadro">
        <div className="editor__rolagem">
          <table className="tabela tabela--itens tabela--cards">
            <thead>
              <tr>
                <th scope="col">Referência</th>
                <th scope="col">Formato</th>
                <th scope="col" className="num">
                  Quantidade
                </th>
                <th scope="col" className="num">
                  Desconto
                </th>
                {/*
                  A coluna de escolha existe nos DOIS casos desde a S4 — só
                  muda o nome e o campo. No del credere ela é a coluna de
                  preço; nas demais é a comissão do representante.
                */}
                <th scope="col">{delCredere ? 'Coluna (del credere)' : 'Comissão'}</th>
                {/*
                  As colunas RT e Frete SAÍRAM — Story C.

                  Eram editáveis em R$/m² por linha (itens 03/04), o modelo
                  errado, e desde a Story B a API recusa esses campos: as
                  células estavam quebradas. Os dois viraram campos do
                  cabeçalho, em % e R$/kg.

                  Os TOTAIS de RT e frete continuam no rodapé — ali são
                  resultado calculado, não entrada, e o vendedor precisa vê-los.
                */}
                <th scope="col" className="num">
                  Preço base
                </th>
                <th scope="col" className="num">
                  Unitário
                </th>
                <th scope="col" className="num">
                  Faturado
                </th>
                <th scope="col" className="num">
                  Total
                </th>
                {editavel && <th scope="col" aria-label="Ações" />}
              </tr>
            </thead>
            <tbody>
              {orcamento.itens.length === 0 ? (
                <tr>
                  {/*
                    ⚠️ Estava `(editavel ? 11 : 10) + (delCredere ? 1 : 0)` e
                    sobrava DOIS — resquício das células de RT e frete,
                    removidas na Story C. O navegador trunca colSpan excedente
                    em silêncio, então nunca apareceu no desktop.

                    São OITO fixas (referência, formato, quantidade, desconto,
                    preço base, unitário, faturado, total), mais a coluna de
                    escolha — que desde a S4 existe sempre — e a de ações.
                  */}
                  <td colSpan={9 + (editavel ? 1 : 0)} className="editor__sem-itens">
                    Nenhum item ainda. Use a busca acima para adicionar o primeiro.
                  </td>
                </tr>
              ) : (
                orcamento.itens.map((item) => (
                  <LinhaItem key={item.itemId} item={item} editavel={editavel} modo={modo} />
                ))
              )}
            </tbody>
          </table>
        </div>

        <footer className="totais">
          <div className="totais__item">
            <span className="totais__rotulo">Medida total</span>
            <span className="totais__valor num">{moedaBr(orcamento.totais.medidaTotal)}</span>
          </div>
          <div className="totais__item">
            <span className="totais__rotulo">Peso total (kg)</span>
            <span className="totais__valor num">{moedaBr(orcamento.totais.pesoTotal)}</span>
          </div>
          <div className="totais__item">
            <span className="totais__rotulo">Frete</span>
            <span className="totais__valor num">{moedaBr(orcamento.totais.freteTotal)}</span>
          </div>
          <div className="totais__item">
            <span className="totais__rotulo">RT</span>
            <span className="totais__valor num">{moedaBr(orcamento.totais.rtTotal)}</span>
          </div>
          <div className="totais__item totais__item--destaque">
            <span className="totais__rotulo">Valor total</span>
            <span className="totais__valor num">{moedaBr(orcamento.totais.valorTotal)}</span>
          </div>
        </footer>
      </div>
    </section>
  );
}
